import { useState, useEffect } from 'react'
import { api, type ApiSite } from '../utils/api'

// ── §70: Project Dashboard ────────────────────────────────────────────────────

interface SiteStats {
  pagesCount: number
  collectionsCount: number
  membersCount: number
  lastDeployment: { status: string; createdAt: string; commitMsg: string | null } | null
  aiUsage: { used: number; limit: number }
}

interface Props {
  site: ApiSite
  onClose: () => void
  onOpenEditor: () => void
  onOpenCms: () => void
  onOpenSettings: () => void
}

const STATUS_COLOR: Record<string, string> = {
  PUBLISHED: '#22c55e',
  DRAFT: '#f59e0b',
  ARCHIVED: '#6b7280',
}
const STATUS_LABEL: Record<string, string> = {
  PUBLISHED: 'Опубликован',
  DRAFT: 'Черновик',
  ARCHIVED: 'Архив',
}

export function SiteDashboardModal({ site, onClose, onOpenEditor, onOpenCms, onOpenSettings }: Props) {
  const [stats, setStats] = useState<SiteStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.get<{ pages: unknown[] }>(`/sites/${site.id}/pages`).catch(() => ({ pages: [] })),
      api.get<unknown[]>(`/cms/sites/${site.id}/collections`).catch(() => []),
      api.get<{ used: number; limit: number }>('/ai/usage').catch(() => ({ used: 0, limit: 5 })),
    ]).then(([pagesData, collections, aiUsage]) => {
      setStats({
        pagesCount: (pagesData.pages?.length ?? 0),
        collectionsCount: (collections as unknown[]).length,
        membersCount: 0,
        lastDeployment: null,
        aiUsage,
      })
    }).finally(() => setLoading(false))
  }, [site.id])

  const statusColor = STATUS_COLOR[site.status] ?? '#6b7280'
  const domain = site.customDomain ?? `${site.slug}.mybst.ru`
  const siteUrl = site.status === 'PUBLISHED' ? `https://${domain}` : null

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', borderRadius: 16, width: '100%', maxWidth: 620, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%)', position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, color: '#fff', fontSize: 18, cursor: 'pointer', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            {site.favicon && <img src={site.favicon} alt="" style={{ width: 28, height: 28, borderRadius: 6 }} />}
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{site.name}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, padding: '3px 10px', background: 'rgba(255,255,255,0.2)', borderRadius: 20, color: '#fff', fontWeight: 700 }}>
              ● {STATUS_LABEL[site.status] ?? site.status}
            </span>
            {siteUrl && (
              <a href={siteUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', textDecoration: 'underline' }}>
                {domain} ↗
              </a>
            )}
            {!siteUrl && <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{domain}</span>}
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Страниц', value: loading ? '...' : String(stats?.pagesCount ?? 0), icon: '📄' },
              { label: 'Коллекций', value: loading ? '...' : String(stats?.collectionsCount ?? 0), icon: '🗂️' },
              { label: 'AI запросов', value: loading ? '...' : `${stats?.aiUsage.used ?? 0}/${stats?.aiUsage.limit === -1 ? '∞' : stats?.aiUsage.limit ?? 5}`, icon: '✨' },
              { label: 'Статус', value: STATUS_LABEL[site.status] ?? site.status, icon: '●', color: statusColor },
            ].map(card => (
              <div key={card.label} style={{ background: 'var(--bg-2)', borderRadius: 10, padding: '12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{card.icon}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: card.color ?? 'var(--text-primary)', marginBottom: 2 }}>{card.value}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{card.label}</div>
              </div>
            ))}
          </div>

          {/* Site info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {site.gaId && (
              <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)', alignItems: 'center' }}>
                <span>📊</span><span>Google Analytics: <strong style={{ color: 'var(--text-primary)' }}>{site.gaId}</strong></span>
              </div>
            )}
            {site.customDomain && (
              <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)', alignItems: 'center' }}>
                <span>🌐</span>
                <span>Домен: <strong style={{ color: site.customDomainVerified ? '#22c55e' : '#f59e0b' }}>
                  {site.customDomain} {site.customDomainVerified ? '✓ проверен' : '⏳ ожидает'}
                </strong></span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)', alignItems: 'center' }}>
              <span>📅</span><span>Создан: <strong style={{ color: 'var(--text-primary)' }}>{new Date(site.createdAt ?? '').toLocaleDateString('ru')}</strong></span>
            </div>
          </div>

          {/* Quick actions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { label: '✏️ Редактор', action: onOpenEditor, primary: true },
              { label: '🗂️ CMS', action: onOpenCms, primary: false },
              { label: '⚙️ Настройки', action: onOpenSettings, primary: false },
            ].map(btn => (
              <button
                key={btn.label}
                onClick={() => { btn.action(); onClose() }}
                style={{
                  padding: '10px', borderRadius: 9, border: btn.primary ? 'none' : '1px solid var(--border)',
                  background: btn.primary ? 'var(--accent)' : 'var(--bg-2)',
                  color: btn.primary ? '#fff' : 'var(--text-secondary)',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
